import React, { useState, useEffect } from 'react';
import { PlusCircle, Edit2, ClipboardList, X, Search, Package, Monitor } from 'lucide-react';
import { PDFDownloadLink, Document, Page, Text, StyleSheet, Font } from '@react-pdf/renderer';
import robotoRegular from '../assets/fonts/Roboto-Regular.ttf';
import authFetch from '../api';
import { useTranslation } from 'react-i18next';
import ProtectedRoute from '../components/ProtectedRoute';

Font.register({
  family: 'Roboto',
  src: robotoRegular,
  fonts: [{ src: robotoRegular, fontWeight: 'normal' }]
});

const ProtocolPDF = ({ asset }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <Text style={styles.text}>
        Инфинити Енерджи Солар ООД{"\n\n"}
        Приемо-предавателен протокол{"\n\n"}
        Днес, {new Date().toLocaleDateString('bg-BG')}, в гр. Разлог, между:{"\n\n"}
        1. Инфинити Енерджи Солар ООД, с ЕИК/ЕГН 206812512, със седалище/адрес: гр. Благоевград, ул."Братя Иванови № 1, п.к.2700, представляван/о от Георги Калоянов, в качеството му на Управител{"\n\n"}
        и{"\n\n"}
        2. {asset.assignedTo?.fullName || asset.assignedTo?.name || '—'}, {asset.assignedTo?.email || '—'}{"\n\n"}
        На длъжност……………………………………{"\n\n"}
        се състави настоящият приемо-предавателен протокол.{"\n\n"}
        Предаващата страна предава, а приемащата страна приема, както следва:{"\n\n"}
        1. компютър: {asset.type || '—'}, {asset.brand || '—'}, {asset.serialNumber || '—'}{"\n"}
        2. монитор……………………………………{"\n"}
        3. периферия…………………………………{"\n"}
        4. други…………………………………………….{"\n\n"}
        Настоящият протокол се изготви в два еднообразни екземпляра, по един за всяка от страните.{"\n\n\n\n"}
        ……………………………………………..                    ……………………………………………..{"\n"}
        /предаваща страна/                      /приемаща страна/
      </Text>
    </Page>
  </Document>
);

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Roboto',
  },
  text: {
    fontSize: 12,
    lineHeight: 1.6
  }
});

const AdminInventoryContent = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('computer-parts');
  const [assets, setAssets] = useState([]);
  const [fieldAssets, setFieldAssets] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [fieldSearchQuery, setFieldSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showFieldAssetModal, setShowFieldAssetModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [editingFieldAsset, setEditingFieldAsset] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    device: '',
    brand: '',
    model: '',
    serialNumber: ''
  });
  const [fieldAssetForm, setFieldAssetForm] = useState({
    name: '',
    brand: '',
    count: '',
    project: ''
  });
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [projects, setProjects] = useState([]);

  // Debug information
  console.log('🔍 AdminInventory Debug:', {
    activeTab,
    assetsCount: assets.length,
    fieldAssetsCount: fieldAssets.length,
    employeesCount: employees.length,
    workersCount: workers.length,
    projectsCount: projects.length,
    selectedAsset: selectedAsset?.serialNumber,
    editingAsset: editingAsset?.serialNumber,
    editingFieldAsset: editingFieldAsset?.serialNumber,
    showModal,
    showFieldAssetModal
  });

  useEffect(() => {
    fetchAssets();
    fetchPeople();
    fetchProjects();
    fetchFieldAssets();
  }, []);

  const fetchPeople = async () => {
    try {
      console.log('🔍 Fetching people for inventory...');
      
      // Fetch Admins and Employees
      const usersRes = await authFetch('/users');
      
      let usersData;
      if (usersRes && usersRes.data) {
        usersData = usersRes.data;
      } else {
        usersData = usersRes;
      }
      
      console.log('📊 Users data received:', usersData);
      
      const admins = Array.isArray(usersData.users)
        ? usersData.users.filter(user => user.role === 'Admin' || user.role === 'SuperAdmin')
        : [];
      const employees = Array.isArray(usersData.users)
        ? usersData.users.filter(user => user.role === 'Employee')
        : [];
      setEmployees([...admins, ...employees]);

      // Fetch all workers
      const workersRes = await authFetch('/workers');
      
      let workersData;
      if (workersRes && workersRes.data) {
        workersData = workersRes.data;
      } else {
        workersData = workersRes;
      }
      
      console.log('📊 Workers data received:', workersData);
      setWorkers(Array.isArray(workersData) ? workersData : []);
      
    } catch (err) {
      console.error('❌ Failed to fetch people:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      setEmployees([]);
      setWorkers([]);
    }
  };

  const fetchProjects = async () => {
    try {
      console.log('🔍 Fetching projects for field assets...');
      const projectsRes = await authFetch('/projects/list');
      
      let projectsData;
      if (projectsRes && projectsRes.data) {
        projectsData = projectsRes.data;
      } else {
        projectsData = projectsRes;
      }
      
      console.log('📊 Projects data received:', projectsData);
      setProjects(Array.isArray(projectsData.projects) ? projectsData.projects : []);
    } catch (err) {
      console.error('❌ Failed to fetch projects:', err);
      setProjects([]);
    }
  };

  const fetchFieldAssets = async () => {
    try {
      console.log('🔍 Fetching field assets from inventory...');
      // Fetch all inventory items and filter for field assets (type: 'field-asset')
      const res = await authFetch('/inventory');
      
      let inventoryData;
      if (res && res.data) {
        inventoryData = res.data;
      } else {
        inventoryData = res;
      }
      
      console.log('📊 Inventory data received:', inventoryData);
      
      // Filter for field assets (type: 'field-asset')
      const fieldAssetsData = Array.isArray(inventoryData) 
        ? inventoryData.filter(item => item.type === 'field-asset')
        : [];
      
      setFieldAssets(fieldAssetsData);
    } catch (err) {
      console.error('❌ Failed to fetch field assets:', err);
      setFieldAssets([]);
    }
  };

  const fetchAssets = async () => {
    try {
      console.log('🔍 Fetching inventory assets...');
      const res = await authFetch('/inventory');
      
      let assetsData;
      if (res && res.data) {
        assetsData = res.data;
      } else {
        assetsData = res;
      }
      
      console.log('📊 Assets data received:', assetsData);
      setAssets(Array.isArray(assetsData) ? assetsData : []);
    } catch (err) {
      console.error('❌ Failed to fetch inventory:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
    }
  };

  const handleSave = async () => {
    // Validation
    if (!form.device || !form.brand || !form.serialNumber) {
      alert(t('adminInventory.pleaseFillRequiredFields'));
      return;
    }

    const payload = {
      type: form.device,
      brand: form.brand,
      model: form.model,
      serialNumber: form.serialNumber,
      assignedTo: form.name || null,
    };

    try {
      console.log('🔍 Saving asset:', payload);
      
      let updated;
      if (editingAsset) {
        const res = await authFetch.patch(`/inventory/${editingAsset._id}`, payload);
        updated = res.data || res;
      } else {
        const res = await authFetch.post('/inventory', payload);
        updated = res.data || res;
      }

      console.log('✅ Asset saved successfully:', updated);

      setAssets((prev) => {
        if (editingAsset) {
          return prev.map((a) => (a._id === updated._id ? updated : a));
        } else {
          return [...prev, updated];
        }
      });

      setShowModal(false);
      setEditingAsset(null);
      setForm({
        name: '',
        email: '',
        device: '',
        brand: '',
        model: '',
        serialNumber: ''
      });
    } catch (err) {
      console.error('❌ Failed to save asset:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      
      if (err.response?.status === 400 && err.response?.data?.error) {
        alert(`${t('adminInventory.failedToSave')}: ${err.response.data.error}`);
      } else {
        alert(t('adminInventory.failedToSaveAsset'));
      }
    }
  };

  const handleSaveFieldAsset = async () => {
    // Validation
    if (!fieldAssetForm.name || !fieldAssetForm.brand || !fieldAssetForm.count || !fieldAssetForm.project) {
      alert('Please fill all required fields');
      return;
    }

    const payload = {
      type: 'field-asset', // Mark as field asset type
      name: fieldAssetForm.name,
      brand: fieldAssetForm.brand,
      count: parseInt(fieldAssetForm.count),
      project: fieldAssetForm.project,
      // Add other required fields for inventory
      model: fieldAssetForm.name, // Use name as model for field assets
      serialNumber: `FA-${Date.now()}`, // Generate unique serial for field assets
      assignedTo: null, // Field assets are not assigned to specific users
    };

    try {
      console.log('🔍 Saving field asset to inventory:', payload);
      
      let updated;
      if (editingFieldAsset) {
        // Update existing field asset
        const res = await authFetch.patch(`/inventory/${editingFieldAsset._id}`, payload);
        updated = res.data || res;
      } else {
        // Create new field asset
        const res = await authFetch.post('/inventory', payload);
        updated = res.data || res;
      }

      console.log('✅ Field asset saved successfully:', updated);

      // Refresh both assets lists
      await fetchAssets();
      await fetchFieldAssets();

      setShowFieldAssetModal(false);
      setEditingFieldAsset(null);
      setFieldAssetForm({
        name: '',
        brand: '',
        count: '',
        project: ''
      });
    } catch (err) {
      console.error('❌ Failed to save field asset:', err);
      alert('Failed to save field asset');
    }
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setForm({
      name: asset.assignedTo?._id || '',
      email: asset.assignedTo?.email || '',
      device: asset.type || '',
      brand: asset.brand || '',
      model: asset.model || '',
      serialNumber: asset.serialNumber || ''
    });
    setShowModal(true);
  };

  const handleEditFieldAsset = (asset) => {
    setEditingFieldAsset(asset);
    setFieldAssetForm({
      name: asset.name || asset.model || '',
      brand: asset.brand || '',
      count: asset.count?.toString() || '1',
      project: asset.project || ''
    });
    setShowFieldAssetModal(true);
  };

  const filteredAssets = assets.filter(asset => {
    const q = searchQuery.toLowerCase();
    return (
      (asset.assignedTo?.fullName || asset.assignedTo?.name || '').toLowerCase().includes(q) ||
      (asset.assignedTo?.email || '').toLowerCase().includes(q) ||
      (asset.type || '').toLowerCase().includes(q) ||
      (asset.brand || '').toLowerCase().includes(q) ||
      (asset.model || '').toLowerCase().includes(q) ||
      (asset.serialNumber || '').toLowerCase().includes(q)
    );
  });

  const filteredFieldAssets = fieldAssets.filter(asset => {
    const q = fieldSearchQuery.toLowerCase();
    return (
      (asset.name || asset.model || '').toLowerCase().includes(q) ||
      (asset.brand || '').toLowerCase().includes(q) ||
      (asset.project || '').toLowerCase().includes(q)
    );
  });

  // Group field assets by project for export
  const getProjectAssets = (projectName) => {
    return fieldAssets.filter(asset => asset.project === projectName);
  };

  // Export all assets from a specific project
  const exportProjectAssets = (projectName) => {
    const projectAssets = getProjectAssets(projectName);
    
    return (
      <PDFDownloadLink
        document={
          <Document>
            <Page size="A4" style={styles.page}>
              <Text style={styles.text}>
                Infinity Energy Solar{"\n\n"}
                Project Assets Report{"\n\n"}
                Project: {projectName}{"\n"}
                Total Assets: {projectAssets.length}{"\n"}
                Generated: {new Date().toLocaleDateString('bg-BG')}{"\n\n"}
                {projectAssets.map((asset, index) => (
                  `${index + 1}. ${asset.name || asset.model} - ${asset.brand} (Count: ${asset.count})${"\n"}`
                )).join('')}
              </Text>
            </Page>
          </Document>
        }
        fileName={`ProjectAssets_${projectName.replace(/\s+/g, '_')}.pdf`}
        className="text-blue-600 hover:text-blue-900 text-sm"
      >
        Export Project
      </PDFDownloadLink>
    );
  };

  // Get unique projects from field assets
  const uniqueProjects = [...new Set(fieldAssets.map(asset => asset.project).filter(Boolean))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center pt-16">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('adminInventory.inventoryManagement')}</h1>
          <p className="text-gray-600 mt-2">{t('adminInventory.manageCompanyAssets')}</p>
        </div>
        {activeTab === 'computer-parts' && (
          <button
            onClick={() => setShowModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
          >
            <PlusCircle size={20} />
            {t('adminInventory.addNewAsset')}
          </button>
        )}
        {activeTab === 'field-assets' && (
          <button
            onClick={() => setShowFieldAssetModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
          >
            <PlusCircle size={20} />
            Add New Field Asset
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('computer-parts')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'computer-parts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Monitor size={16} />
            Computer Parts
          </button>
          <button
            onClick={() => setActiveTab('field-assets')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
              activeTab === 'field-assets'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Package size={16} />
            Assets on Field
          </button>
        </nav>
      </div>

      {/* Computer Parts Tab */}
      {activeTab === 'computer-parts' && (
        <>
          {/* Search */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder={t('adminInventory.searchAssets')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Assets Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminInventory.name')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminInventory.email')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminInventory.device')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminInventory.brand')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminInventory.model')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminInventory.serialNumber')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminInventory.actions')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminInventory.protocol')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAssets.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center">
                        <ClipboardList className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('adminInventory.noAssetsFound')}</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {searchQuery 
                            ? t('adminInventory.tryAdjustingSearch')
                            : t('adminInventory.getStartedAddingAsset')
                          }
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredAssets
                      .sort((a, b) => {
                        const nameA = (a.assignedTo?.fullName || a.assignedTo?.name || '').toLowerCase();
                        const nameB = (b.assignedTo?.fullName || b.assignedTo?.name || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                      .map((asset) => (
                      <tr 
                        key={asset._id} 
                        className="hover:bg-gray-50"
                        onClick={() => setSelectedAsset(asset)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {asset.assignedTo?.fullName || asset.assignedTo?.name || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {asset.assignedTo?.email || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {asset.type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {asset.brand}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {asset.model}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {asset.serialNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(asset);
                              }}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit Asset"
                            >
                              <Edit2 size={16} />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <span onClick={(e) => e.stopPropagation()}>
                            <PDFDownloadLink
                              document={<ProtocolPDF asset={asset} />}
                              fileName={`AProtocol_${(asset.assignedTo?.fullName || asset.assignedTo?.name || 'employee').replace(/\s+/g, '_')}.pdf`}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              {t('adminInventory.download')}
                            </PDFDownloadLink>
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {filteredAssets.length > 0 && (
            <div className="text-sm text-gray-600 font-semibold">
              {t('adminInventory.totalAssets')}: {filteredAssets.length}
            </div>
          )}
        </>
      )}

      {/* Field Assets Tab */}
      {activeTab === 'field-assets' && (
        <>
          {/* Search */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search field assets..."
                value={fieldSearchQuery}
                onChange={e => setFieldSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Field Assets Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Asset Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brand
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Project
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Export
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredFieldAssets.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <Package className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No field assets found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {fieldSearchQuery 
                            ? 'Try adjusting your search terms'
                            : 'No field assets available'
                          }
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredFieldAssets
                      .sort((a, b) => (a.name || a.model || '').localeCompare(b.name || b.model || ''))
                      .map((asset) => (
                      <tr key={asset._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {asset.name || asset.model}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {asset.brand}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {asset.count}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {asset.project}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditFieldAsset(asset);
                              }}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit Field Asset"
                            >
                              <Edit2 size={16} />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <PDFDownloadLink
                            document={
                              <Document>
                                <Page size="A4" style={styles.page}>
                                  <Text style={styles.text}>
                                    Infinity Energy Solar{"\n\n"}
                                    Field Asset Report{"\n\n"}
                                    Asset: {asset.name || asset.model}{"\n"}
                                    Brand: {asset.brand}{"\n"}
                                    Count: {asset.count}{"\n"}
                                    Project: {asset.project}{"\n"}
                                    Generated: {new Date().toLocaleDateString('bg-BG')}
                                  </Text>
                                </Page>
                              </Document>
                            }
                            fileName={`FieldAsset_${(asset.name || asset.model).replace(/\s+/g, '_')}.pdf`}
                            className="text-green-600 hover:text-green-900"
                          >
                            Export PDF
                          </PDFDownloadLink>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Project Export Section */}
          {uniqueProjects.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Export Assets by Project</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {uniqueProjects.map((projectName) => {
                  const projectAssets = getProjectAssets(projectName);
                  return (
                    <div key={projectName} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-medium text-gray-900">{projectName}</h4>
                        <span className="text-sm text-gray-500">{projectAssets.length} assets</span>
                      </div>
                      <div className="text-sm text-gray-600 mb-3">
                        {projectAssets.slice(0, 3).map((asset, index) => (
                          <div key={index} className="truncate">
                            {asset.name || asset.model} - {asset.brand} (x{asset.count})
                          </div>
                        ))}
                        {projectAssets.length > 3 && (
                          <div className="text-gray-400">...and {projectAssets.length - 3} more</div>
                        )}
                      </div>
                      {exportProjectAssets(projectName)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {filteredFieldAssets.length > 0 && (
            <div className="text-sm text-gray-600 font-semibold">
              Total Field Assets: {filteredFieldAssets.length}
            </div>
          )}
        </>
      )}

      {/* Add/Edit Asset Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingAsset ? t('adminInventory.editAsset') : t('adminInventory.addNewAsset')}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminInventory.employeeWorker')}</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.name}
                    onChange={(e) => {
                      // Search both employees (admins+employees) and workers
                      const selected = [...employees, ...workers].find(person => person._id === e.target.value);
                      setForm({
                        ...form,
                        name: selected?._id || '',
                        email: selected?.email || '',
                      });
                    }}
                  >
                    <option value="">{t('adminInventory.selectEmployeeAdminWorker')}</option>
                    {[
                      ...employees.map(emp => ({
                        ...emp,
                        displayName: (emp.fullName || emp.name || emp.email) + ' (' + emp.role + ')'
                      })),
                      ...workers.map(worker => ({
                        ...worker,
                        displayName: (worker.name || worker.email) + ' (Worker)'
                      }))
                    ]
                      .sort((a, b) => a.displayName.localeCompare(a.displayName, 'bg'))
                      .map(person => (
                        <option key={person._id} value={person._id}>
                          {person.displayName}
                        </option>
                      ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminInventory.email')}</label>
                  <input
                    type="email"
                    placeholder="Email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminInventory.deviceType')}</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.device}
                    onChange={(e) => setForm({ ...form, device: e.target.value })}
                    required
                  >
                    <option value="">{t('adminInventory.selectDeviceType')}</option>
                    <option value="Laptop">Laptop</option>
                    <option value="Monitor">Monitor</option>
                    <option value="Keyboard">Keyboard</option>
                    <option value="Mouse">Mouse</option>
                    <option value="Headset">Headset</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminInventory.brand')}</label>
                  <input
                    type="text"
                    placeholder={t('adminInventory.enterBrandName')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.brand}
                    onChange={(e) => setForm({ ...form, brand: e.target.value })}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminInventory.model')}</label>
                  <input
                    type="text"
                    placeholder={t('adminInventory.enterModelName')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminInventory.serialNumber')}</label>
                  <input
                    type="text"
                    placeholder={t('adminInventory.enterSerialNumber')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={form.serialNumber}
                    onChange={(e) => setForm({ ...form, serialNumber: e.target.value })}
                    required
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    {t('adminInventory.cancel')}
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingAsset ? t('adminInventory.updateAsset') : t('adminInventory.createAsset')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Field Asset Modal */}
      {showFieldAssetModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingFieldAsset ? 'Edit Field Asset' : 'Add New Field Asset'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Asset Name</label>
                  <input
                    type="text"
                    placeholder="Enter asset name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    value={fieldAssetForm.name}
                    onChange={(e) => setFieldAssetForm({ ...fieldAssetForm, name: e.target.value })}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                  <input
                    type="text"
                    placeholder="Enter brand name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    value={fieldAssetForm.brand}
                    onChange={(e) => setFieldAssetForm({ ...fieldAssetForm, brand: e.target.value })}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Count</label>
                  <input
                    type="number"
                    placeholder="Enter count"
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    value={fieldAssetForm.count}
                    onChange={(e) => setFieldAssetForm({ ...fieldAssetForm, count: e.target.value })}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    value={fieldAssetForm.project}
                    onChange={(e) => setFieldAssetForm({ ...fieldAssetForm, project: e.target.value })}
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map((project) => (
                      <option key={project._id} value={project.name}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowFieldAssetModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveFieldAsset}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    {editingFieldAsset ? 'Update Asset' : 'Create Asset'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Asset Modal */}
      {selectedAsset && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t('adminInventory.assetInformation')}</h3>
                <button
                  onClick={() => setSelectedAsset(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('adminInventory.name')}</label>
                    <p className="text-sm text-gray-900">{selectedAsset.assignedTo?.fullName || selectedAsset.assignedTo?.name || '—'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('adminInventory.email')}</label>
                    <p className="text-sm text-gray-900">{selectedAsset.assignedTo?.email || '—'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('adminInventory.device')}</label>
                    <p className="text-sm text-gray-900">{selectedAsset.type || '—'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('adminInventory.brand')}</label>
                    <p className="text-sm text-gray-900">{selectedAsset.brand || '—'}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('adminInventory.model')}</label>
                    <p className="text-sm text-gray-900">{selectedAsset.model || '—'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('adminInventory.serialNumber')}</label>
                    <p className="text-sm text-gray-900">{selectedAsset.serialNumber || '—'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('adminInventory.addedAt')}</label>
                    <p className="text-sm text-gray-900">{selectedAsset.createdAt ? new Date(selectedAsset.createdAt).toLocaleString('bg-BG') : '—'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('adminInventory.updatedAt')}</label>
                    <p className="text-sm text-gray-900">{selectedAsset.updatedAt ? new Date(selectedAsset.updatedAt).toLocaleString('bg-BG') : '—'}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setSelectedAsset(null)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  {t('adminInventory.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminInventory = () => {
  return (
    <ProtectedRoute pageKey="inventory">
      <AdminInventoryContent />
    </ProtectedRoute>
  );
};

export default AdminInventory;